import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Types
type ThemeMode = 'light' | 'dark';

interface ThemeState {
  theme: ThemeMode;
  isLoading: boolean;
}

interface ThemeContextType extends ThemeState {
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

// Initial state
const initialState: ThemeState = {
  theme: 'light',
  isLoading: true
};

// Action types
type ThemeAction =
  | { type: 'SET_THEME'; payload: ThemeMode }
  | { type: 'SET_LOADING'; payload: boolean };

// Reducer
const themeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
  switch (action.type) {
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
        isLoading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    default:
      return state;
  }
};

// Create context
const ThemeContext = createContext<ThemeContextType | null>(null);

// Theme Provider Component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Apply theme to document
  const applyTheme = (theme: ThemeMode) => {
    document.documentElement.setAttribute('data-theme', theme);
  };

  // Initialize theme on app load
  useEffect(() => {
    const initializeTheme = () => {
      // Check localStorage first
      const savedTheme = localStorage.getItem('theme') as ThemeMode | null;
      
      // If no saved theme, check system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const defaultTheme: ThemeMode = savedTheme || (systemPrefersDark ? 'dark' : 'light');
      
      // Apply theme immediately to prevent flash
      applyTheme(defaultTheme);
      
      // Update state
      dispatch({ type: 'SET_THEME', payload: defaultTheme });
    };

    initializeTheme();
  }, []);

  // Listen for system theme changes if no saved preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        const newTheme: ThemeMode = e.matches ? 'dark' : 'light';
        applyTheme(newTheme);
        dispatch({ type: 'SET_THEME', payload: newTheme });
      }
    };

    // Add listener
    mediaQuery.addEventListener('change', handleChange);
    
    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Set theme function
  const setTheme = (theme: ThemeMode) => {
    // Apply theme immediately
    applyTheme(theme);
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
    
    // Update state
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme: ThemeMode = state.theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const contextValue: ThemeContextType = {
    ...state,
    setTheme,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};